<template>
  <div>
    <!-- Day modal -->
    <Teleport to="body">
      <div
        v-if="isDayModalVisible"
        class="modal-backdrop"
        @click="isDayModalVisible = false"
      />
      <div v-if="isDayModalVisible" class="modal">
        <div class="modal-header">
          <span>{{ selectedDayDate }}</span>
          <button class="close-btn" @click="isDayModalVisible = false">
            ✕
          </button>
        </div>
        <div
          class="modal-body"
          style="padding: 0; overflow-y: auto; max-height: 60vh"
        >
          <button
            v-for="event in selectedDayEvents"
            :key="event._id"
            class="day-ev"
            :class="{ 'day-ev-online': event.type === 'online' }"
            @click="
              isDayModalVisible = false;
              openEventModal(event);
            "
          >
            <div class="day-ev-name">{{ event.name }}</div>
            <div class="day-ev-meta">
              <span class="type-badge">{{
                event.type === 'online' ? 'EN LIGNE' : 'SUR PLACE'
              }}</span>
              <span v-if="event.duration && event.duration !== '1'" class="tag"
                >{{ event.duration }}J</span
              >
              <span v-if="event.departementCode || event.zipCode" class="tag">
                {{ event.departementCode || event.zipCode.substring(0, 2) }}
              </span>
            </div>
          </button>
        </div>
      </div>
    </Teleport>

    <!-- Event detail modal -->
    <Teleport to="body">
      <div
        v-if="isModalVisible"
        class="modal-backdrop"
        @click="isModalVisible = false"
      />
      <div v-if="isModalVisible" class="modal">
        <div class="modal-header">
          <span
            style="
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              flex: 1;
            "
            >{{ selectedEvent?.name?.toUpperCase() }}</span
          >
          <button class="close-btn" @click="isModalVisible = false">✕</button>
        </div>
        <div
          v-if="selectedEvent"
          class="modal-body"
          style="display: flex; flex-direction: column; gap: 16px"
        >
          <!-- Date -->
          <div
            style="
              font-family: var(--mono);
              font-size: 11px;
              letter-spacing: 0.1em;
              text-transform: uppercase;
              color: var(--ink-dim);
            "
          >
            {{ formatEventDate(selectedEvent) }}
          </div>

          <!-- Tags -->
          <EventTags :event="selectedEvent" size="md" />

          <!-- Location -->
          <div v-if="selectedEvent.location">
            <a
              style="
                color: var(--accent);
                font-size: 13px;
                text-decoration: underline;
              "
              :href="`https://www.google.com/maps/search/?api=1&query=${selectedEvent.location}`"
              target="_blank"
              >{{ selectedEvent.location }}</a
            >
          </div>
          <div
            v-else-if="selectedEvent.type !== 'online'"
            style="color: var(--ink-dim); font-size: 13px; font-style: italic"
          >
            Localisation introuvable
          </div>

          <!-- CTA -->
          <a
            :href="selectedEvent.eventLink"
            target="_blank"
            class="btn btn-primary"
            style="width: 100%; margin-top: 4px; justify-content: center"
            @click="trackClickEvent(selectedEvent)"
          >
            Voir l'événement →
          </a>
        </div>
      </div>
    </Teleport>

    <!-- Weekday headers -->
    <div class="cal-header">
      <div v-for="day in weekDays" :key="day" class="cal-dow">{{ day }}</div>
    </div>

    <!-- Calendar grid -->
    <div class="cal-grid" :class="{ 'cal-week': calendarView === 'week' }">
      <div
        v-for="day in calendarDays"
        :key="day.date"
        class="cal-cell"
        :class="{
          'cal-cell-other': !day.isCurrentMonth,
          'cal-cell-today': day.isToday,
          'cal-cell-week': calendarView === 'week',
          'cal-cell-selected': day.date === props.selectedDate,
        }"
        @click="emit('select-day', day.date)"
      >
        <!-- Date row -->
        <div class="cal-date-row">
          <span class="cal-date-num">{{ day.dayNumber }}</span>
          <span v-if="day.events.length > 0" class="cal-date-badge">{{
            day.events.length
          }}</span>
        </div>

        <!-- Events (desktop) -->
        <div>
          <button
            v-for="event in day.events.slice(
              0,
              calendarView === 'week' ? 10 : 3,
            )"
            :key="event._id"
            class="cal-ev"
            :class="{ 'cal-ev-online': event.type === 'online' }"
            @click.stop="openEventModal(event)"
          >
            {{ event.name }}
            <span v-if="calendarView === 'week' && event.duration">
              · {{ event.duration }}J</span
            >
          </button>
          <button
            v-if="day.events.length > (calendarView === 'week' ? 10 : 3)"
            class="cal-ev-more"
            @click.stop="openDayModal(day)"
          >
            +{{ day.events.length - (calendarView === 'week' ? 10 : 3) }} de
            plus
          </button>
        </div>

        <!-- Event dots (mobile, month view only) -->
        <div v-if="calendarView === 'month'" class="cal-cell-dots">
          <span
            v-for="dot in getDots(day)"
            :key="dot.key"
            class="cal-dot"
            :class="dot.class"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs, { type Dayjs } from 'dayjs';
import type { EventItem } from '@/types/Event';

interface EventsByDate {
  date: string;
  events: EventItem[];
  count: number;
}

interface CalendarDay {
  date: string;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: EventItem[];
}

const props = defineProps<{
  eventsByDate: EventsByDate[];
  calendarView: 'month' | 'week';
  currentDate: Dayjs;
  selectedDate: string | null;
}>();

const emit = defineEmits<{ 'select-day': [date: string] }>();

const {
  proxy: { gtag },
} = useScriptGoogleAnalytics();

const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const isModalVisible = ref(false);
const selectedEvent = ref<EventItem | null>(null);
const isDayModalVisible = ref(false);
const selectedDayEvents = ref<EventItem[]>([]);
const selectedDayDate = ref('');

useEscapeKey(isDayModalVisible, () => {
  isDayModalVisible.value = false;
});
useEscapeKey(isModalVisible, () => {
  isModalVisible.value = false;
});

const eventsMap = computed(() => {
  const map = new Map<string, EventItem[]>();
  props.eventsByDate.forEach((day) => map.set(day.date, day.events));
  return map;
});

const calendarDays = computed<CalendarDay[]>(() => {
  const days: CalendarDay[] = [];
  const today = dayjs().format('YYYY-MM-DD');

  let startDate: Dayjs;
  let endDate: Dayjs;

  if (props.calendarView === 'month') {
    startDate = props.currentDate.startOf('month').startOf('isoWeek');
    endDate = props.currentDate.endOf('month').endOf('isoWeek');
  } else {
    startDate = props.currentDate.startOf('isoWeek');
    endDate = props.currentDate.endOf('isoWeek');
  }

  let current = startDate;
  while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
    const dateStr = current.format('YYYY-MM-DD');
    days.push({
      date: dateStr,
      dayNumber: current.date(),
      isCurrentMonth: current.month() === props.currentDate.month(),
      isToday: dateStr === today,
      events: eventsMap.value.get(dateStr) || [],
    });
    current = current.add(1, 'day');
  }
  return days;
});

const openEventModal = (event: EventItem) => {
  selectedEvent.value = event;
  isModalVisible.value = true;
};

const openDayModal = (day: CalendarDay) => {
  selectedDayEvents.value = day.events;
  selectedDayDate.value = dayjs(day.date)
    .format('dddd D MMMM YYYY')
    .toUpperCase();
  isDayModalVisible.value = true;
};

interface DotDescriptor {
  key: number;
  class: Record<string, boolean>;
}

function getDots(day: CalendarDay): DotDescriptor[] {
  const { events } = day;
  if (events.length === 0) return [];
  const maxDots = 4;
  const showCount = Math.min(events.length, maxDots);
  const hasOverflow = events.length > maxDots;
  return Array.from({ length: showCount }, (_, i) => {
    const ev = events[i]!;
    const isOverflowIndicator = hasOverflow && i === maxDots - 1;
    return {
      key: i,
      class: {
        'cal-dot-onsite': ev.type === 'onSite' && !isOverflowIndicator,
        'cal-dot-online': ev.type === 'online' && !isOverflowIndicator,
        'cal-dot-dim': isOverflowIndicator,
      },
    };
  });
}

const formatEventDate = (event: EventItem) => {
  const start = dayjs(event.startDate);
  const end = dayjs(event.endDate);
  if (start.isSame(end, 'day'))
    return start.format('dddd D MMMM YYYY').toUpperCase();
  return `Du ${start.format('D MMMM')} au ${end.format('D MMMM YYYY')}`.toUpperCase();
};

const trackClickEvent = (event: EventItem) => {
  gtag('event', 'open_event', {
    event_category: 'engagement',
    event_label: event.name,
    event_context: 'calendar',
  });
};
</script>
